const META_KEYS = new Set(['__touched', 'id', '_localId']);
const TEMPLATE_PROMPT_VALUES = new Set([
  'Ava & Julian',
  'A little favor from our day',
  'Thank you for being here. Open the story, sign the blessing wall, and keep this as a tiny piece of tonight.',
  'Begin with the photo that feels like the day: the couple, the tables, the flowers, the venue, or one quiet moment before the celebration.',
  'Thank You for Being Here',
  'Thank you for being part of the room where this promise became real. We made this little digital favor so you can open a few pieces of the day, leave us a blessing, and keep a small memory from the celebration.',
  'From Ceremony to Celebration',
  'A few places that tell the story.',
  'Memory route',
  'Ceremony',
  'Reception',
  'Add the moment, place, or photo where the promise was made.',
  'Add the table, toast, dance floor, or small detail guests will remember.',
  'Open Tonight',
  'Open Later',
  'Open after the last song',
  'Open on our first anniversary',
  'Open when you wonder if it mattered',
  'Open when the day feels heavy',
  'Open when you want to smile',
  'Thank you for celebrating with us. Your presence made the room feel warmer, louder, and more full of love.',
  'A small reminder that this day was not only ours. It was held by everyone who showed up, cheered, danced, and wished us well.',
  'It mattered. The small things, the repeated things, the things nobody clapped for. I carry more of your love than I probably say out loud.',
  'I hope this brings you back to the truth: you have been the steady place in so many memories, and you deserve to feel held too.',
  'One tiny proof that you are loved on normal days, not only holidays.',
  'A Voice Note for Mom',
  'A Voice Note',
  'I have something to tell you...',
  'Record the part that would mean more in your real voice: thank you, I love you, or the memory she should hear again.',
  'Record a short message they can replay later.',
  'Wedding Blessing Wall',
  'Leave a wish, memory, toast, or tiny note from your table.',
  'Family Blessing Wall',
  'Notes from people invited into this gift.',
  'Guestbook',
  'Sign the Book',
  'Leave a blessing',
  'Leave a short blessing, wish, or memory for this gift.',
  'Write a short wish for the couple. Messages can be reviewed before they appear publicly.',
  'Thank you for the blessing',
  'Your note has been saved for the couple to keep with this wedding favor.',
  'Keep This Favor',
  'Keep This Close',
  'With Love',
  'Thank you for holding this day with us.',
  'Save this little card as a piece of the celebration.',
  'For every time you need proof that your love has been seen.',
  'Come back here whenever you need to feel appreciated.',
]);

const normalizePromptValue = (value) => String(value || '').replace(/\s+/g, ' ').trim();

const MEDIA_FIELDS = [
  'url',
  'audioUrl',
  'videoUrl',
  'imageUrl',
  'mediaUrl',
  'coverImage',
  'coverUrl',
  'bgUrl',
  'backgroundImageUrl',
  'photoUrl',
  'beforeImage',
  'afterImage',
  'beforeImageUrl',
  'afterImageUrl',
  'recordedUrl',
  'photos',
  'images',
  'videos',
  'audios',
  'items',
];

const MEDIA_TYPES = new Set([
  'primary_media',
  'hero_media',
  'media_card',
  'voice_postcard',
  'song_dedication',
  'frame_gallery',
  'then_now',
]);

const TOUCH_REQUIRED_TYPES = new Set([
  'text',
  'guestbook',
  'keepsake_poster',
  'surprise_box',
  'memory_map',
  'timeline',
  'tiny_museum',
  'polaroid_booth',
  'flip_card',
]);

const stripMeta = (value) => {
  if (Array.isArray(value)) {
    return value.map(stripMeta);
  }
  if (value && typeof value === 'object') {
    return Object.entries(value).reduce((acc, [key, child]) => {
      if (!META_KEYS.has(key)) {
        acc[key] = stripMeta(child);
      }
      return acc;
    }, {});
  }
  return value;
};

export const hasMeaningfulValue = (value) => {
  if (Array.isArray(value)) {
    return value.some((item) => {
      if (typeof item === 'string') {
        const normalized = normalizePromptValue(item);
        return normalized.length > 0 && !TEMPLATE_PROMPT_VALUES.has(normalized);
      }
      if (item && typeof item === 'object') return hasMeaningfulValue(Object.values(item));
      return Boolean(item);
    });
  }
  if (value && typeof value === 'object') {
    return Object.entries(value).some(([key, child]) => !META_KEYS.has(key) && hasMeaningfulValue(child));
  }
  if (typeof value === 'string') {
    const normalized = normalizePromptValue(value);
    return normalized.length > 0 && !TEMPLATE_PROMPT_VALUES.has(normalized);
  }
  return Boolean(value);
};

export const hasModuleMedia = (module) => {
  const data = module?.data || {};
  return MEDIA_FIELDS.some((field) => hasMeaningfulValue(data[field]));
};

export const isModuleComplete = (module) => {
  if (!module) return false;

  const data = module.data || {};
  const touched = Boolean(data.__touched);
  const cleanedData = stripMeta(data);

  if (module.type === 'guestbook') {
    return Boolean(data.inviteToken) || Boolean(data.entries?.length) || (touched && hasMeaningfulValue(cleanedData));
  }

  if (module.type === 'voice_postcard') {
    if (hasMeaningfulValue([data.audioUrl, data.recordedUrl, data.voiceUrl, data.audio?.url])) return true;
    const hasCoverImage = hasMeaningfulValue([data.backgroundImageUrl, data.bgUrl, data.imageUrl, data.photoUrl]);
    const hasCustomCopy = hasMeaningfulValue([data.title, data.prompt, data.message]);
    return touched && hasCoverImage && hasCustomCopy;
  }

  if (MEDIA_TYPES.has(module.type)) {
    return hasModuleMedia(module);
  }

  if (module.type === 'memory_map') {
    return touched && hasMeaningfulValue(data.stops || data.timelineItems || []);
  }

  if (module.type === 'timeline') {
    return touched && hasMeaningfulValue(data.timelineItems || []);
  }

  if (module.type === 'polaroid_booth') {
    return Boolean(data.entries?.length) || (touched && hasMeaningfulValue(cleanedData));
  }

  if (TOUCH_REQUIRED_TYPES.has(module.type)) {
    return touched && hasMeaningfulValue(cleanedData);
  }

  return touched && hasMeaningfulValue(cleanedData);
};

export const getCompletedModuleCount = (modules = []) => (
  modules.filter(isModuleComplete).length
);
